/**
 * The console's access rules, with no framework in them.
 *
 * Deliberately free of `next/headers`, `next/navigation` and any server import: the same
 * three functions are needed by the middleware (edge runtime), the console layout
 * (server component) and the sign-in action, and a shared rule that each surface
 * reimplements is a rule that eventually disagrees with itself. Being pure is also what
 * makes them testable without a DOM or a request.
 */

/** httpOnly, so the token is never readable from JavaScript in the page (ADR-007). */
export const ADMIN_SESSION_COOKIE = 'kadir_admin_session';

export const ADMIN_LOGIN_PATH = '/login';

export const ADMIN_HOME_PATH = '/';

/** Set by the middleware so a server component can see the URL it is rendering for. */
export const ADMIN_PATHNAME_HEADER = 'x-kadir-admin-pathname';

const ALLOWED_CONSOLE_PATHS = [
  '/',
  '/reports',
  '/verification',
  '/documents',
  '/support',
  '/talents',
  '/organizations',
  '/subscriptions',
  '/roles',
  '/settings',
  '/admin',
];

export function safeReturnTo(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//') || value.startsWith('\\') || value.includes('\\')) return null;
  if (
    value === ADMIN_LOGIN_PATH ||
    value.startsWith(`${ADMIN_LOGIN_PATH}?`) ||
    value === '/admin/login' ||
    value.startsWith('/admin/login?')
  ) {
    return null;
  }
  const pathWithoutQuery = value.split('?')[0]!;
  const isConsolePath =
    pathWithoutQuery === '/' ||
    pathWithoutQuery.startsWith('/admin') ||
    ALLOWED_CONSOLE_PATHS.some(
      (p) => p !== '/' && (pathWithoutQuery === p || pathWithoutQuery.startsWith(`${p}/`)),
    );
  if (!isConsolePath) return null;
  return value;
}

/** The login URL, carrying where the request was headed so sign-in can resume it. */
export function loginPathFor(returnTo?: string | null): string {
  const target = safeReturnTo(returnTo);
  return target ? `${ADMIN_LOGIN_PATH}?next=${encodeURIComponent(target)}` : ADMIN_LOGIN_PATH;
}

/**
 * Whether the session cookie carries the `Secure` flag, from `ADMIN_COOKIE_SECURE`.
 *
 * Explicit rather than inferred from `NODE_ENV`, and the reason is the local stack:
 * `docker compose` runs the web container with `NODE_ENV=production` because `next
 * start` serves an optimized build, yet it serves it over plain `http://localhost:3000`.
 * A `Secure` cookie there is silently dropped by the browser and sign-in never sticks —
 * a bug that only appears in the one environment nobody deploys.
 *
 * Only the literal string `false` turns it off, so a new environment that forgets the
 * variable gets a `Secure` cookie rather than a plaintext one.
 */
export function adminCookieSecure(value: string | undefined): boolean {
  return value?.trim().toLowerCase() !== 'false';
}
